import jwt from "jwt-decode";
import { useEffect, useState, useMemo, useCallback } from "react";
import { Outlet } from "react-router-dom";
import { Auth as AmplifyAuth } from "aws-amplify";
import { checkValidToken, onTokenRefreshed } from "../api/Auth";
import awsExports from "../aws-exports";
import UserInfoContext, { UserInfo } from "./UserInfoContext";
import { buildUserInfoFromIdToken } from "./buildUserInfoFromIdToken";

export function UserInfoContextProvider() {
  const [userInfo, setUserInfo] = useState<UserInfo>();
  const [render, setRender] = useState<Boolean>(false);

  useEffect(() => {
    const unsubscribe = onTokenRefreshed((newToken: string) => {
      try {
        const decodedToken: any = jwt(newToken);
        setUserInfo(buildUserInfoFromIdToken(decodedToken, newToken));
      } catch (e) {
        console.error("Failed to decode refreshed token in UserInfoProvider", e);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const checkInitialSession = async () => {
      try {
        const lastAuthUser = localStorage.getItem(
          `CognitoIdentityServiceProvider.${awsExports.USER_POOL_APP_CLIENT_ID}.LastAuthUser`
        );
        if (lastAuthUser) {
          const accessToken = localStorage.getItem(
            `CognitoIdentityServiceProvider.${awsExports.USER_POOL_APP_CLIENT_ID}.${lastAuthUser}.idToken`
          );
          if (accessToken && checkValidToken(accessToken)) {
            const decodedToken: any = jwt(accessToken);
            setUserInfo(buildUserInfoFromIdToken(decodedToken, accessToken));
            setRender(true);
            return;
          }
        }
        const session = await AmplifyAuth.currentSession();
        const idToken = session.getIdToken();
        const payload: any = idToken.payload;
        setUserInfo(buildUserInfoFromIdToken(payload, idToken.getJwtToken()));
      } catch {
        // Not authenticated; auth wrapper will handle rendering sign-in
      } finally {
        setRender(true);
      }
    };

    checkInitialSession();
  }, []);

  const updateUserInfo = useCallback((newValue: UserInfo) => {
    setUserInfo(newValue);
    setRender(true);
  }, []);

  const value = useMemo(() => ({ userInfo, updateUserInfo }), [userInfo, updateUserInfo]);

  return (
    <UserInfoContext.Provider value={value}>
      {render && <Outlet />}
    </UserInfoContext.Provider>
  );
}
